import React from "react";
import {useFormState} from "../../hooks/useFormState";
import {useTranslation} from "react-i18next";
import Button from "../../ui/button";
import {HelpCircleIcon} from "../../ui/icons";
import WithTooltip from "../../ui/tooltip";
import FileInput from "../editor/FileInput";

import classes from "../editor/importChallengeModal.module.css";

type ContentProps = React.ComponentProps<"div"> & {
  onImportFile?: (file: File) => void;
  templateUrl?: string;
};

function ImportFileModalHeader() {
  const {t} = useTranslation();
  return <span>{t("components.ImportChallengeModal.title")}</span>;
}

function ImportFileModalContent({onImportFile, templateUrl}: ContentProps) {
  const {t} = useTranslation();

  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [csvError, setCsvError] = React.useState<string | null>(null);

  const formState = useFormState<{ file: File | undefined }>({
    initialValues: {file: undefined},
    preventDefault: true,
    onSubmit: (data) => {
      if (data.file && !csvError) {
        onImportFile && onImportFile(data.file);
      }
    },
  });

  const handleFileChange = async (file?: File) => {
  if (!file) {
    setSelectedFile(null);
    setCsvError(null);
    formState.setValues({file: undefined});
    return;
  }

  if (!file.name.toLowerCase().endsWith(".csv")) {
    setCsvError(t("components.ImportChallengeModal.fileFormatError"));
    setSelectedFile(null);
    formState.setValues({file: undefined});
    return;
  }

  const text = await file.text();
  const rows = text.split(/\r?\n/).filter(Boolean);

  if (rows.length === 0) {
    setCsvError("Файл пустой");
    setSelectedFile(null);
    formState.setValues({file: undefined});
    return;
  }

  const headers = rows[0].split(";").map(h => h.trim().toLowerCase());
  const expectedHeaders = [
    "action_type","competence_name","action_order","question_text","hint_text",
    "avatar_text","system_text","branch_order",
    "reference_text","branch_hint_text","branch_state"
  ];

  const isHeadersValid = expectedHeaders.every((h) => headers.includes(h));
  if (!isHeadersValid) {
    const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
    setCsvError(`${t("components.ImportChallengeModal.expectedHeadersError")}: ${missingHeaders.join(", ")}`);
    setSelectedFile(null);
    formState.setValues({file: undefined});
    return;
  }

  setCsvError(null);
  setSelectedFile(file);
  formState.setValues({file});
};

  const submitDisabled = !selectedFile || !!csvError;

  return (
    <form className={classes.root} method="post" onSubmit={formState.submitHandler}>

      <label className={classes.helpText}>
        {t("components.ImportChallengeModal.template.label")}
        <WithTooltip
          as="span"
          className={classes.help}
          helpText={t("components.ImportChallengeModal.template.helpText")}
        >
          <HelpCircleIcon />
        </WithTooltip>
      </label>
      {
        <p>
          <a
            href={templateUrl || ""}
            target="_blank"
            rel="noopener noreferrer"
            download="competence_test_template.csv"
          >
            {t("components.ImportChallengeModal.template.download")}
          </a>
        </p>
      }

      <FileInput
        name="file"
        variant="any"
        formats={["csv"]}
        accept=".csv"
        setValues={(data) => handleFileChange(data.file)}
        onDelete={() => handleFileChange(undefined)}
      />

      {csvError && <span className={classes.error}>{csvError}</span>}

      <Button
        type="submit"
        className={classes.button}
        color="success"
        disabledColor="secondary"
        disabled={submitDisabled}
      >
        {t("components.ImportChallengeModal.apply")}
      </Button>
    </form>
  );
}

const ImportFileModal = {
  Header: ImportFileModalHeader,
  Content: ImportFileModalContent,
};

export default ImportFileModal;
